// Loads the cached professor script (Mongo + object storage) and preloads its audio; built-in fallback text always present.
import FALLBACK from './professorFallback.json';
import { decodeQuiet } from '@/spore/eerieAudio';
import { cachedAudio, cachedJson, withTimeout } from './showCache';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/professor`;
export const stripTags = (t) => t.replace(/\[[^\]]*\]/g, '').replace(/\s+/g, ' ').trim();

export function createProfessorPreloader({ concurrency = 6, timeoutMs = 15000 } = {}) {
  const entries = new Map(FALLBACK.plan.map((it) => [it.id, { id: it.id, text: FALLBACK.lines[it.id].text, guess: FALLBACK.lines[it.id].guess, source: 'builtin', voiceSource: 'none', audioPath: null, buffer: null, state: 'pending' }]));
  let scriptId = null;
  let startedAt = null;
  let finishedAt = null;

  async function loadAudio(e, retries = 2, ms = timeoutMs) {
    if (e.loading) return e.loading;
    e.loading = (async () => {
      for (let i = 0; i <= retries; i += 1) {
        try {
          const { buf, local } = await cachedAudio(e.audioPath, ms);
          e.buffer = await withTimeout(decodeQuiet(buf), ms);
          e.state = 'ready';
          e.cached = true;
          e.local = local;
          return e.buffer;
        } catch {
          if (i < retries) await new Promise((r) => setTimeout(r, 800 * 2 ** i));
        }
      }
      e.state = 'failed';
      return null;
    })().finally(() => {
      e.loading = null;
    });
    return e.loading;
  }

  async function run(script = null) {
    startedAt = performance.now();
    let doc = script;
    if (!doc) {
      try {
        doc = (await cachedJson('/professor/script/current', timeoutMs)).doc; // the copy saved on this laptop first
      } catch {
        doc = null;
      }
    }
    if (doc) {
      scriptId = doc.script_id;
      doc.lines.forEach((l) => {
        const e = entries.get(l.id);
        if (e) Object.assign(e, { text: l.text, guess: l.guess, source: l.text_source, voiceSource: l.voice_source, audioPath: l.audio_path });
      });
    }
    const queue = [...entries.values()].filter((e) => {
      if (e.audioPath) return true;
      e.state = 'failed';
      return false;
    });
    await Promise.all(Array.from({ length: concurrency }, async () => {
      while (queue.length) await loadAudio(queue.shift());
    }));
    finishedAt = performance.now();
    return !!doc;
  }

  return {
    run,
    get: (id) => entries.get(id),
    // never waits on the network mid-show: not in memory → subtitles now, the clip keeps loading in the background
    ensureVoice: (id) => {
      const e = entries.get(id);
      if (!e.buffer && e.audioPath && e.state === 'failed') loadAudio(e, 1, 8000);
      return Promise.resolve(e.buffer);
    },
    cached: () => [...entries.values()].filter((e) => e.cached).length,
    retryFailed: () => [...entries.values()].filter((e) => e.audioPath && e.state === 'failed').forEach((e) => loadAudio(e, 2)),
    scriptId: () => scriptId,
    voiceSources: () => Object.fromEntries([...entries.values()].map((e) => [e.id, e.buffer ? e.voiceSource : 'subtitles'])),
    progress() {
      let done = 0;
      let voices = 0;
      let available = 0;
      let failed = 0;
      entries.forEach((e) => {
        if (e.state !== 'pending') done += 1;
        if (e.state === 'ready') voices += 1;
        if (e.audioPath) available += 1;
        if (e.audioPath && e.state === 'failed') failed += 1;
      });
      const ms = startedAt === null ? 0 : Math.round((finishedAt || performance.now()) - startedAt);
      return { done, total: entries.size, voices, voicesAvailable: available, voicesFailed: failed, ms, complete: done === entries.size, scriptId };
    },
  };
}

export async function generateScript(onProgress, action = 'generate') {
  const start = await (await fetch(`${API}/script/${action}`, { method: 'POST' })).json();
  for (;;) {
    await new Promise((r) => setTimeout(r, 3000));
    const st = await (await fetch(`${API}/script/status/${start.job_id}`)).json();
    onProgress(st);
    if (st.status === 'ready') break;
    if (st.status === 'failed') throw new Error(st.error || 'generation failed');
  }
  return (await fetch(`${API}/script/current`)).json();
}
